import { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Select from '#globalComponents/Forms/Select';
import RadioButton from '#globalComponents/RadioButton/RadioButton';
import HeaderFilter from '#globalComponents/Table/filters/HeaderFilter';
import { useDebouncedValue } from '#globalHooks/useDebouncedValue';

import { CONTACT_CHANNEL_FILTER_OPTIONS, MIN_FILTER_DATE, RATING_MIN_OPTIONS } from '../../../constants/leadFilters';
import { DateInputFilter } from '../searchFilters/components/common/DateInputFilter';
import { RangeInputFilter } from '../searchFilters/components/common/RangeInputFilter';

// Each typed control keeps a local draft so keystrokes never thrash the URL;
// the debounced commit only fires when the draft diverges from the incoming
// value (no spurious commit on mount). Popovers unmount on close, so drafts
// re-initialize from the URL on every open.
const useDebouncedCommit = (draft, commit, delay) => {
	const debounced = useDebouncedValue(draft, delay);
	const commitRef = useRef(commit);
	commitRef.current = commit;
	const initialRef = useRef(draft);
	useEffect(() => {
		if (debounced === initialRef.current) return;
		initialRef.current = debounced;
		commitRef.current(debounced);
	}, [debounced]);
};

// Longer than the numeric controls: a date is typed segment by segment, and the
// pause while moving from day to month to year exceeds the default delay.
const DATE_COMMIT_DELAY_MS = 600;

const toDraft = (value, unitFactor) => (value === undefined ? '' : String(value / unitFactor));

const MinMaxFilterControl = ({
	heading,
	minParamKey,
	maxParamKey,
	minValue,
	maxValue,
	unit,
	unitFactor = 1,
	onChange,
}) => {
	const [draft, setDraft] = useState({
		min: toDraft(minValue, unitFactor),
		max: toDraft(maxValue, unitFactor),
	});

	useDebouncedCommit(draft, ({ min, max }) => {
		const parse = (raw) => {
			if (raw === '') return undefined;
			const num = Number.parseFloat(raw);
			return Number.isFinite(num) && num >= 0 ? num * unitFactor : undefined;
		};
		onChange({ [minParamKey]: parse(min), [maxParamKey]: parse(max) });
	});

	return (
		<div>
			<label htmlFor={`hf-${minParamKey}`} className="block text-xs text-bgBase-800 mb-1">
				{heading}
				{unit ? ` (${unit})` : ''}
			</label>
			<RangeInputFilter
				fromId={`hf-${minParamKey}`}
				toId={`hf-${maxParamKey}`}
				fromValue={draft.min}
				toValue={draft.max}
				onFromChange={(value) => setDraft((prev) => ({ ...prev, min: value }))}
				onToChange={(value) => setDraft((prev) => ({ ...prev, max: value }))}
				fromPlaceholder="Von"
				toPlaceholder="Bis"
				min={0}
			/>
		</div>
	);
};

MinMaxFilterControl.propTypes = {
	heading: PropTypes.string.isRequired,
	minParamKey: PropTypes.string.isRequired,
	maxParamKey: PropTypes.string.isRequired,
	minValue: PropTypes.number,
	maxValue: PropTypes.number,
	unit: PropTypes.string,
	unitFactor: PropTypes.number,
	onChange: PropTypes.func.isRequired,
};

const MinSliderFilterControl = ({ heading, paramKey, value, onChange }) => {
	const [draft, setDraft] = useState(value ?? 0);

	useDebouncedCommit(draft, (committed) => {
		onChange({ [paramKey]: committed > 0 ? committed : undefined });
	});

	return (
		<div>
			<label htmlFor={`hf-${paramKey}`} className="block text-xs text-bgBase-800 mb-1">
				{heading}
				{draft > 0 ? ` ≥ ${draft}%` : ''}
			</label>
			<input
				id={`hf-${paramKey}`}
				type="range"
				min={0}
				max={100}
				step={5}
				value={draft}
				onChange={(event) => setDraft(Number(event.target.value))}
				className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
			/>
			<div className="flex justify-between text-[10px] text-bgBase-800 mt-0.5">
				<span>0%</span>
				<span>100%</span>
			</div>
		</div>
	);
};

MinSliderFilterControl.propTypes = {
	heading: PropTypes.string.isRequired,
	paramKey: PropTypes.string.isRequired,
	value: PropTypes.number,
	onChange: PropTypes.func.isRequired,
};

const MinSelectFilterControl = ({ heading, paramKey, value, onChange }) => (
	<div>
		<label htmlFor={`hf-${paramKey}`} className="block text-xs text-bgBase-800 mb-1">
			{heading}
		</label>
		<Select
			id={`hf-${paramKey}`}
			value={value ?? ''}
			onChange={(event) =>
				onChange({ [paramKey]: event.target.value === '' ? undefined : Number(event.target.value) })
			}
		>
			<option value="">Egal</option>
			{RATING_MIN_OPTIONS.map((rating) => (
				<option key={rating} value={rating}>
					≥ {rating.toLocaleString('de-DE')} ★
				</option>
			))}
		</Select>
	</div>
);

MinSelectFilterControl.propTypes = {
	heading: PropTypes.string.isRequired,
	paramKey: PropTypes.string.isRequired,
	value: PropTypes.number,
	onChange: PropTypes.func.isRequired,
};

// A date input fires on every keystroke once its other segments are filled, so
// typing a year emits 0002, 0020, 0202 before 2026 — each a valid date the URL
// would otherwise commit mid-edit. The draft debounces those partials away and
// the MIN_FILTER_DATE floor rejects any that outlive the delay. Incomplete input
// reads as '' from the browser and commits as undefined, never a wrong year.
// min/max also cross-bind the two inputs so an inverted range cannot be picked.
const DateRangeFilterControl = ({ heading, fromParamKey, toParamKey, fromValue, toValue, onChange }) => {
	const [draft, setDraft] = useState({ from: fromValue ?? '', to: toValue ?? '' });

	useDebouncedCommit(
		`${draft.from}|${draft.to}`,
		() => {
			onChange({ [fromParamKey]: draft.from || undefined, [toParamKey]: draft.to || undefined });
		},
		DATE_COMMIT_DELAY_MS,
	);

	return (
		<div className="space-y-2">
			<span className="block text-xs text-bgBase-800">{heading}</span>
			<DateInputFilter
				id={`hf-${fromParamKey}`}
				label="Von"
				value={draft.from}
				min={MIN_FILTER_DATE}
				max={draft.to}
				onChange={(value) => setDraft((prev) => ({ ...prev, from: value }))}
			/>
			<DateInputFilter
				id={`hf-${toParamKey}`}
				label="Bis"
				value={draft.to}
				min={draft.from || MIN_FILTER_DATE}
				onChange={(value) => setDraft((prev) => ({ ...prev, to: value }))}
			/>
		</div>
	);
};

DateRangeFilterControl.propTypes = {
	heading: PropTypes.string.isRequired,
	fromParamKey: PropTypes.string.isRequired,
	toParamKey: PropTypes.string.isRequired,
	fromValue: PropTypes.string,
	toValue: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};

const CONTACTS_FILTER_OPTIONS = [
	{ id: 'all', label: 'Alle', params: { hasContacts: undefined, contactChannel: undefined } },
	{ id: 'yes', label: 'Mit Kontakten', params: { hasContacts: true, contactChannel: undefined } },
	{ id: 'no', label: 'Ohne Kontakte', params: { hasContacts: false, contactChannel: undefined } },
	...CONTACT_CHANNEL_FILTER_OPTIONS.map((channel) => ({
		id: channel.value,
		label: `Ansprechpartner mit ${channel.label}`,
		params: { hasContacts: undefined, contactChannel: channel.value },
	})),
];

const selectedContactsOption = (hasContacts, contactChannel) => {
	if (contactChannel !== undefined) return contactChannel;
	if (hasContacts === undefined) return 'all';
	return hasContacts ? 'yes' : 'no';
};

// One exclusive choice over two params: a channel already implies contacts, so
// the yes/no and channel filters never combine.
const ContactsFilterControl = ({ hasContacts, contactChannel, onChange }) => {
	const selected = selectedContactsOption(hasContacts, contactChannel);
	return (
		<HeaderFilter label="Nach „Kontakte“ filtern" active={selected !== 'all'}>
			<span className="block text-xs text-bgBase-800 mb-1.5">Kontakte</span>
			<div className="space-y-1">
				{CONTACTS_FILTER_OPTIONS.map((option) => (
					<RadioButton
						key={option.id}
						id={`hf-contacts-${option.id}`}
						name="hf-contacts"
						value={option.id}
						size="sm"
						checked={selected === option.id}
						onChange={() => onChange(option.params)}
						label={option.label}
					/>
				))}
			</div>
		</HeaderFilter>
	);
};

ContactsFilterControl.propTypes = {
	hasContacts: PropTypes.bool,
	contactChannel: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};

export {
	MinMaxFilterControl,
	MinSliderFilterControl,
	MinSelectFilterControl,
	DateRangeFilterControl,
	ContactsFilterControl,
};
